import { ALL_ACTIONS, type PolicyActionEnum } from './actions.js';
import { type EvaluableStatement, PolicyEffectEnum } from './document.js';
import { expandActionPattern, matchesPattern } from './pattern.js';
import { ANY_RESOURCE } from './resources.js';

/**
 * The only surface consumers depend on. Swapping the engine (e.g. for casbin once
 * conditions are needed) means providing another implementation of this
 * interface from {@link createPolicyEvaluator}; the koa and react entry points don't change.
 */
export interface PolicyEvaluator {
  /**
   * True when some statement allows `action` on `resource` and none denies it. An explicit Deny
   * always wins, and anything not explicitly allowed is denied.
   */
  isAllowed(action: PolicyActionEnum, resource?: string): boolean;
  /** True when every action is allowed. True for an empty list. */
  isAllowedAll(actions: readonly PolicyActionEnum[], resource?: string): boolean;
  /** Every catalog action allowed on any resource. */
  allowedActions(): PolicyActionEnum[];
}

class StatementEvaluator implements PolicyEvaluator {
  private readonly statements: readonly EvaluableStatement[];

  constructor(statements: readonly EvaluableStatement[]) {
    this.statements = statements;
  }

  isAllowed(action: PolicyActionEnum, resource: string = ANY_RESOURCE): boolean {
    const matching = this.statements.filter(
      (statement) =>
        statement.resources.some((pattern) => matchesPattern(pattern, resource)) &&
        statement.actions.some((pattern) => matchesPattern(pattern, action))
    );
    // Fail closed: any matching statement that isn't an Allow blocks the action.
    return (
      matching.some((statement) => statement.effect === PolicyEffectEnum.Allow) &&
      matching.every((statement) => statement.effect === PolicyEffectEnum.Allow)
    );
  }

  isAllowedAll(actions: readonly PolicyActionEnum[], resource?: string): boolean {
    return actions.every((action) => this.isAllowed(action, resource));
  }

  allowedActions(): PolicyActionEnum[] {
    return ALL_ACTIONS.filter((action) => this.isAllowed(action));
  }
}

export function createPolicyEvaluator(statements: readonly EvaluableStatement[]): PolicyEvaluator {
  return new StatementEvaluator(statements);
}

class AllowedActionsEvaluator implements PolicyEvaluator {
  private readonly allowed: ReadonlySet<string>;

  constructor(allowedActions: readonly string[]) {
    this.allowed = new Set(allowedActions);
  }

  isAllowed(action: PolicyActionEnum): boolean {
    return this.allowed.has(action);
  }

  isAllowedAll(actions: readonly PolicyActionEnum[]): boolean {
    return actions.every((action) => this.isAllowed(action));
  }

  allowedActions(): PolicyActionEnum[] {
    return ALL_ACTIONS.filter((action) => this.allowed.has(action));
  }
}

/**
 * An evaluator over actions the server has already resolved, e.g. from
 * `createPolicyEvaluator(statements).allowedActions()`. Wildcards, and whatever the engine grows
 * to support, are the server's concern; the client only does a set lookup. It answers for every
 * resource, which is all a policy can express today. Per-resource checks should come from the
 * server (flags on the resource, or a check endpoint) rather than from this evaluator.
 *
 * Actions this build doesn't know are accepted and never match, so a newer server can't break an
 * older client.
 */
export function createAllowedActionsEvaluator(allowedActions: readonly string[]): PolicyEvaluator {
  return new AllowedActionsEvaluator(allowedActions);
}

/** An evaluator that allows nothing, for anonymous or unresolved callers. */
export const DENY_ALL_EVALUATOR: PolicyEvaluator = createPolicyEvaluator([]);

function expandStatementActions(
  statements: readonly EvaluableStatement[],
  effect?: PolicyEffectEnum
): Set<PolicyActionEnum> {
  const actions = new Set<PolicyActionEnum>();
  for (const statement of statements) {
    if (effect !== undefined && statement.effect !== effect) continue;
    for (const pattern of statement.actions) {
      for (const action of expandActionPattern(pattern)) actions.add(action);
    }
  }
  return actions;
}

/**
 * Escalation guard: the actions `statements` mention that `granter` doesn't hold itself.
 * Wildcards are expanded against the catalog first, so `mgmt:brand:*` requires every brand action.
 * Deny statements count too: an actor may only write rules, either way, about actions it holds.
 */
export function findUngrantableActions(
  granter: PolicyEvaluator,
  statements: readonly EvaluableStatement[]
): PolicyActionEnum[] {
  return [...expandStatementActions(statements)].filter((action) => !granter.isAllowed(action)).sort();
}

/**
 * Escalation guard for removals: the actions denied in `before` but no longer in `after`, that
 * `granter` doesn't hold. Lifting a Deny widens access just as granting an Allow does, so
 * detaching, deleting or editing a policy that denies something needs the same authority.
 */
export function findUnliftableDenies(
  granter: PolicyEvaluator,
  before: readonly EvaluableStatement[],
  after: readonly EvaluableStatement[] = []
): PolicyActionEnum[] {
  const stillDenied = expandStatementActions(after, PolicyEffectEnum.Deny);
  return [...expandStatementActions(before, PolicyEffectEnum.Deny)]
    .filter((action) => !stillDenied.has(action) && !granter.isAllowed(action))
    .sort();
}
